// @ts-strict-ignore
import {
  Button,
  ButtonVariant,
  IconName,
} from '@usealloy/component-library';
import React, {
  useCallback, useEffect, useState,
} from 'react';

import {
  selectDiffMode,
  selectIsPendingHistoryCommit,
  selectPreviewPrecedenceIndexedAction,
} from 'flow/store/redux/selectors';
import { resetNodeAndConnectorState } from 'flow/store/redux/slice';
import {
  commitWorkflowHistoryTransaction,
  cancelMultiStepActionPreviewThunk,
} from 'flow/store/redux/thunks';

import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import IslandActionRuleEditor from '../../IslandActionRuleEditor';
import type { BaseOutcomeIslandProps } from '../types';
import { ActionType } from '~/dashboard/views/workflows/store/engine/constants';
import type { Action } from '~/dashboard/views/workflows/store/engine/types';
import BaseComplexIsland from '../../BaseComplexIsland';
import { useEditorAccess } from 'flow/editor/context';
import { getIsEditable } from 'flow/service/general';
import { DiffOutcomeActionsSplitView } from 'flow/editor/components/DiffView/DiffSplitView/DiffSplitView';
import { UserContext } from '~/dashboard/context';
import { Operation } from 'flow/service/changelog/types';
import { createUiEffectContext } from 'flow/service/history';
import { changeDescription } from 'flow/store/redux/types';

import { handleOnAddOutcomeClick as createOutcomePreview } from './helpers';


interface ComplexOutcomeIslandProps extends BaseOutcomeIslandProps { }

export default function ComplexOutcomeIsland({
  outcomeActions,
}: ComplexOutcomeIslandProps) {
  const dispatch = useAppDispatch();
  const { featureFlags } = React.useContext(UserContext);
  const previewPrecedenceIndexedAction = useAppSelector(
    selectPreviewPrecedenceIndexedAction,
  );
  const previewOutcomeAction = previewPrecedenceIndexedAction?.type === ActionType.OUTCOME
    ? previewPrecedenceIndexedAction
    : undefined;
  const reduxPreviewOutcomePublicId = previewOutcomeAction?.publicId;
  const isInDiffMode = useAppSelector(selectDiffMode);
  const isPendingHistoryCommit = useAppSelector(selectIsPendingHistoryCommit);

  const { editorMode } = useEditorAccess();
  const inEditMode = getIsEditable(editorMode);

  const [
    previewOutcomeActionPublicId, setPreviewOutcomeActionPublicId,
  ] = useState<Action['publicId']>(previewOutcomeAction?.publicId ?? null);
  const isAddingNewOutcome = Boolean(previewOutcomeActionPublicId);
  const isTrackedPreviewActionValid = !previewOutcomeActionPublicId
    || outcomeActions.some(({ publicId }) => publicId === previewOutcomeActionPublicId);
  useEffect(() => {
    if (!isTrackedPreviewActionValid) {
      setPreviewOutcomeActionPublicId(null);
      return;
    }

    if (reduxPreviewOutcomePublicId) {
      setPreviewOutcomeActionPublicId(reduxPreviewOutcomePublicId);
    }
  }, [
    isTrackedPreviewActionValid,
    reduxPreviewOutcomePublicId,
  ]);

  const cancelPreview = useCallback(() => {
    if (previewOutcomeActionPublicId) {
      dispatch(cancelMultiStepActionPreviewThunk(previewOutcomeActionPublicId));
      setPreviewOutcomeActionPublicId(null);
    }
  }, [dispatch, previewOutcomeActionPublicId]);

  const handleDrawerToggle = useCallback(() => {
    cancelPreview();
    dispatch(resetNodeAndConnectorState());
  }, [cancelPreview, dispatch]);

  const handleOnAddOutcomeClick = useCallback(() => {
    const publicId = createOutcomePreview(
      dispatch,
      outcomeActions,
      Boolean(featureFlags?.workflowEditorUndoRedo),
    );
    if (!publicId) return;

    setPreviewOutcomeActionPublicId(publicId);
  }, [dispatch, featureFlags?.workflowEditorUndoRedo, outcomeActions]);

  const handleOnSubmitAction = useCallback(() => {
    if (previewOutcomeActionPublicId) {
      dispatch(commitWorkflowHistoryTransaction({
        context: {
          operation: Operation.Add,
          uiEffectContext: createUiEffectContext(changeDescription.action, {
            publicId: previewOutcomeActionPublicId,
          }),
        },
      }));
    }

    setPreviewOutcomeActionPublicId(null);
  }, [dispatch, previewOutcomeActionPublicId]);

  const handleOnCancelAction = cancelPreview;

  return (
    <BaseComplexIsland
      onDrawerToggle={handleDrawerToggle}
      title="Outcomes"
      data-dd-privacy="allow"
      isInDiffMode={isInDiffMode}
    >
      {inEditMode && !isInDiffMode && (
        <Button
          disabled={isAddingNewOutcome}
          icon={IconName.Add}
          onClick={handleOnAddOutcomeClick}
          variant={ButtonVariant.tertiary}
          data-dd-action-name="outcome-island-add-outcome"
        >
          Add Outcomes
        </Button>
      )}
      {isInDiffMode ? (
        <DiffOutcomeActionsSplitView>
          {action => (
            <IslandActionRuleEditor
              action={action}
              actionType={ActionType.OUTCOME}
              allActionsOfActionType={outcomeActions}
              isPreviewAction={previewOutcomeActionPublicId === action.publicId}
              isInteractionDisabled={Boolean(
                isPendingHistoryCommit
                && reduxPreviewOutcomePublicId !== action.publicId,
              )}
              onCancelAction={handleOnCancelAction}
              onSubmitAction={handleOnSubmitAction}
              isInDiffMode={isInDiffMode}
            />
          )}
        </DiffOutcomeActionsSplitView>
      ) : (
        outcomeActions.map(outcomeAction => (
          <IslandActionRuleEditor
            key={outcomeAction.publicId}
            action={outcomeAction}
            actionType={ActionType.OUTCOME}
            allActionsOfActionType={outcomeActions}
            isPreviewAction={previewOutcomeActionPublicId === outcomeAction.publicId}
            isInteractionDisabled={Boolean(
              isPendingHistoryCommit
              && reduxPreviewOutcomePublicId !== outcomeAction.publicId,
            )}
            onCancelAction={handleOnCancelAction}
            onSubmitAction={handleOnSubmitAction}
            isInDiffMode={isInDiffMode}
          />
        ))
      )}
    </BaseComplexIsland>
  );
}
